import { useEffect, useRef, useState } from "react";
import { Upload, Trash2, Clapperboard, RefreshCw, Loader2, Film } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { mediaSrc, useHero } from "@/components/HeroVideo";

const CATS = [["general", "General"], ["trail-rides", "Trail Rides"], ["concerts", "Concerts"], ["corporate", "Corporate"], ["sports", "Sports & Arena"]];
const inputCls = "rounded-lg border border-neutral-700 bg-black px-3 py-2 text-sm text-stone-100 outline-none focus:border-cyan-500";

export function MediaManager() {
  const [media, setMedia] = useState([]);
  const [category, setCategory] = useState("general");
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState([]);
  const [generating, setGenerating] = useState(false);
  const fileRef = useRef();
  const videoRef = useRef();
  const [tick, setTick] = useState(0);
  const hero = useHero(true, tick);

  const load = () => api.get("/media").then((r) => setMedia(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f); fd.append("category", category); fd.append("label", f.name);
        await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
      }
      toast.success(`${files.length} file(s) uploaded.`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const del = async (id) => {
    await api.delete(`/admin/media/${id}`);
    setSelected((s) => s.filter((x) => x !== id));
    load();
  };

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : s.length < 3 ? [...s, id] : s));

  const generate = async () => {
    setGenerating(true);
    try {
      const sources = media.filter((m) => selected.includes(m.id)).map((m) => ({ image_url: m.url, prompt: "" }));
      const r = await api.post("/admin/hero/generate", { sources });
      toast.success(`Generating ${r.data.clips} clip(s) with fal.ai — this takes a few minutes.`);
      setTick((t) => t + 1);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setGenerating(false);
    }
  };

  const uploadVideo = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      await api.post("/admin/hero/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000 });
      toast.success("Hero footage replaced.");
      setTick((t) => t + 1);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const images = media.filter((m) => m.kind === "image");
  const status = hero?.status || "idle";

  return (
    <div className="mt-6 grid gap-6 lg:grid-cols-12" data-testid="media-manager">
      {/* HERO PANEL */}
      <div className="rounded-2xl border border-cyan-500/20 bg-[#0a0a0a] p-5 lg:col-span-5" data-testid="hero-media-panel">
        <div className="flex items-center gap-2"><Clapperboard className="h-5 w-5 text-cyan-500" /><h3 className="font-display text-lg uppercase text-chrome">Hero Video</h3></div>
        <div className="mt-3 flex items-center gap-2">
          <span className={`rounded-full px-3 py-1 font-mono-label text-[10px] uppercase ${status === "ready" ? "bg-green-500/20 text-green-400" : status === "generating" ? "bg-cyan-500/20 text-cyan-300" : status === "error" ? "bg-red-500/20 text-red-400" : "bg-stone-700 text-neutral-300"}`} data-testid="hero-status">
            {status === "generating" && <Loader2 className="mr-1 inline h-3 w-3 animate-spin" />}{status}
          </span>
          {hero?.updated_at && <span className="text-xs text-neutral-500">updated {new Date(hero.updated_at).toLocaleString()}</span>}
        </div>
        {hero?.video_url && (
          <video src={mediaSrc(hero.video_url)} poster={mediaSrc(hero.poster_url)} muted loop autoPlay playsInline controls className="mt-4 w-full rounded-xl border border-white/10" data-testid="hero-preview-video" />
        )}
        <ul className="mt-3 space-y-1 font-mono-label text-[11px] text-neutral-400" data-testid="hero-log">
          {(hero?.log || []).map((l, i) => <li key={i}>› {l}</li>)}
        </ul>
        <p className="mt-4 text-xs text-neutral-400">Select up to 3 photos in the library, then generate. Each photo becomes a 6s cinematic clip (fal.ai), stitched into a seamless loop.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button onClick={generate} disabled={generating || status === "generating"} data-testid="hero-generate-btn"
            className="btn-gold flex items-center gap-2 rounded-full px-4 py-2 font-display text-sm uppercase disabled:opacity-50">
            <RefreshCw className="h-4 w-4" /> {selected.length ? `Generate from ${selected.length} photo(s)` : "Generate from latest photos"}
          </button>
          <button onClick={() => videoRef.current?.click()} disabled={busy} data-testid="hero-upload-btn"
            className="flex items-center gap-2 rounded-full border border-stone-600 px-4 py-2 font-display text-sm uppercase text-stone-200 hover:border-cyan-500">
            <Film className="h-4 w-4" /> Upload real footage
          </button>
          <input ref={videoRef} type="file" accept="video/mp4,video/webm" className="hidden" onChange={(e) => uploadVideo(e.target.files[0])} data-testid="hero-upload-input" />
        </div>
      </div>

      {/* LIBRARY */}
      <div className="rounded-2xl border border-neutral-800 bg-[#0a0a0a] p-5 lg:col-span-7" data-testid="media-library">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-display text-lg uppercase text-chrome">Photo Library</h3>
          <div className="flex items-center gap-2">
            <select value={category} onChange={(e) => setCategory(e.target.value)} className={inputCls} data-testid="media-category-select">
              {CATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="media-upload-btn"
              className="btn-gold flex items-center gap-2 rounded-full px-4 py-2 font-display text-sm uppercase disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload photos
            </button>
            <input ref={fileRef} type="file" multiple accept="image/*" className="hidden" onChange={(e) => upload([...e.target.files])} data-testid="media-upload-input" />
          </div>
        </div>
        <p className="mt-2 text-xs text-neutral-500">Tag photos by vertical — they appear on the homepage category tiles automatically. Click to select for the hero video.</p>
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {images.map((m) => (
            <div key={m.id} className={`group relative overflow-hidden rounded-xl border ${selected.includes(m.id) ? "border-cyan-400 ring-2 ring-cyan-500/50" : "border-neutral-800"}`} data-testid={`media-item-${m.id}`}>
              <img src={mediaSrc(m.url)} alt={m.label} onClick={() => toggle(m.id)} className="h-28 w-full cursor-pointer object-cover" />
              <div className="absolute bottom-0 left-0 right-0 bg-black/70 px-2 py-1 font-mono-label text-[9px] uppercase text-neutral-300">{m.category}</div>
              <button onClick={() => del(m.id)} data-testid={`media-delete-${m.id}`} className="absolute right-1 top-1 rounded-md bg-black/70 p-1 text-red-400 opacity-0 transition-opacity group-hover:opacity-100"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
          {!images.length && <p className="col-span-full text-sm text-neutral-500" data-testid="media-empty">No photos yet — upload the owner's real event photos to replace the stock imagery.</p>}
        </div>
      </div>
      {/* MUSIC */}
      <div className="rounded-2xl border border-neutral-800 bg-[#0a0a0a] p-5 lg:col-span-12" data-testid="music-manager">
        <TrackManager />
      </div>
    </div>
  );
}

function TrackManager() {
  const [tracks, setTracks] = useState([]);
  const [busy, setBusy] = useState(false);
  const [artist, setArtist] = useState("");
  const ref = useRef();
  const load = () => api.get("/admin/tracks").then((r) => setTracks(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f); fd.append("artist", artist); fd.append("title", f.name.replace(/\.[^.]+$/, ""));
        await api.post("/admin/tracks", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000 });
      }
      toast.success(`${files.length} track(s) added to the site playlist.`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-display text-lg uppercase text-chrome">Background Music</h3>
          <p className="mt-1 text-xs text-neutral-500">By default the site streams zydeco through official Spotify players (legal, free). Upload MP3s you hold the rights to and they replace the Spotify player.</p>
        </div>
        <div className="flex items-center gap-2">
          <input placeholder="Artist name" value={artist} onChange={(e) => setArtist(e.target.value)} className={inputCls} data-testid="track-artist-input" />
          <button onClick={() => ref.current?.click()} disabled={busy} data-testid="track-upload-btn" className="btn-gold flex items-center gap-2 rounded-full px-4 py-2 font-display text-sm uppercase disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload tracks
          </button>
          <input ref={ref} type="file" multiple accept="audio/*" className="hidden" onChange={(e) => upload([...e.target.files])} data-testid="track-upload-input" />
        </div>
      </div>
      <div className="mt-4 space-y-2">
        {tracks.map((t) => (
          <div key={t.id} className="flex items-center justify-between rounded-xl border border-neutral-800 bg-black/30 px-4 py-2 text-sm" data-testid={`track-item-${t.id}`}>
            <span className="text-stone-200">{t.title} <span className="text-neutral-500">· {t.artist}</span></span>
            <div className="flex items-center gap-3">
              <audio src={mediaSrc(t.url)} controls preload="none" className="h-8" />
              <button onClick={() => api.delete(`/admin/tracks/${t.id}`).then(load)} data-testid={`track-delete-${t.id}`} className="text-red-400"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
        {!tracks.length && <p className="text-sm text-neutral-500" data-testid="tracks-empty">No custom tracks — the site is using the official Spotify "Trail Ride Vibe" player.</p>}
      </div>
    </>
  );
}
